import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { expertTipTopicApi } from '../services/adminApi';
import { queryKeys } from '../utils/queryKeys';
import type { ExpertTipTopicFilters, ExpertTipTopicFormData } from '../types/expert-tip-topic';

export function useExpertTipTopics(filters: Partial<ExpertTipTopicFilters> = {}) {
  return useQuery({
    queryKey: queryKeys.expertTipTopics.list(filters),
    queryFn: () => expertTipTopicApi.listTopics(filters),
    placeholderData: (previousData) => previousData,
  });
}

export function useActiveTopics() {
  return useQuery({
    queryKey: queryKeys.expertTipTopics.active(),
    queryFn: () => expertTipTopicApi.getActiveTopics(),
  });
}

export function useCreateExpertTipTopic() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ExpertTipTopicFormData) => expertTipTopicApi.createTopic(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.expertTipTopics.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.expertTipTopics.active() });
    },
  });
}

export function useUpdateExpertTipTopic() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ExpertTipTopicFormData }) =>
      expertTipTopicApi.updateTopic(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.expertTipTopics.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.expertTipTopics.active() });
    },
  });
}

export function useUpdateExpertTipTopicStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isDeleted }: { id: string; isDeleted: boolean }) =>
      expertTipTopicApi.updateTopicStatus(id, isDeleted),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.expertTipTopics.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.expertTipTopics.active() });
      queryClient.invalidateQueries({ queryKey: queryKeys.expertTipTopicSchedules.lists() });
    },
  });
}
